import type {
  CostLine,
  HomeBuyingCostOutput,
  IndicativeRange,
} from "./types.js";

// Plain-English result copy for the home-buying-cost composer. Lives in the
// rules package so every surface (Expo result screen, "my results" list, PDF
// report) renders identical wording from one source — drift between channels is
// the information → advice kill-threat (validation §3).
//
// Regulatory guardrails baked into the wording:
//  - Information, not advice. We STATE the itemised cited line totals and how
//    they were derived. NEVER "you can afford", "you save", "you should buy".
//  - The total is a RANGE (LMI + misc are indicative) — we never collapse it to
//    a single "your cost is $X" quote.
//  - The first-home concession / FHOG are framed as differences between cited
//    figures and gated maximums, never personalised payouts.
//  - Every line cites its source module / authority; the mandatory disclaimer
//    renders on every surface.

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** An indicative range rendered as "A$X – A$Y", never a single figure. */
export function formatRange(r: IndicativeRange): string {
  return `${formatAud(r.low)} – ${formatAud(r.high)}`;
}

/**
 * The mandatory disclaimer rendered on every home-buying-cost surface (CLAUDE.md
 * regulatory guardrails / validation §3 — binding, kill-threat if absent). The
 * composite total-cash-to-buy figure is the archetypal mortgage-broker lead
 * hook, so the information→advice line + the no-credit-product posture are
 * stated explicitly, matching the sibling stamp-duty / first-home-scheme modules.
 */
export const HOME_BUYING_COST_DISCLAIMER =
  "Educational tool. Not financial advice. This is general information that adds " +
  "up the standard upfront costs of buying a home from official figures — it is " +
  "not a recommendation about any home loan, lender, broker or product, and not " +
  "an assessment of whether a purchase is affordable for you. Duty, grants, fees and LMI " +
  "change and vary by provider — the total is an indicative range, never a quote. " +
  "Verify every figure with your state revenue office, the scheme's own site and " +
  "your own conveyancer before you act.";

export function headline(out: HomeBuyingCostOutput): string {
  // A range, never a single quote — the total carries two genuinely indicative
  // inputs (LMI + misc), so a point figure would be a disguised quote.
  return `Estimated upfront cash to complete a ${formatAud(out.purchasePrice)} purchase in ${out.state}: ${formatRange(out.totalCashToComplete)} (indicative).`;
}

/** One itemised line, framed as a cited fact (cash out or government help). */
export function lineBullet(line: CostLine): string {
  const shown = line.range ? formatRange(line.range) : formatAud(Math.abs(line.amount));
  const sign = line.amount < 0 ? "less " : "";
  return `${line.label}: ${sign}${shown}${line.indicative ? " (indicative)" : ""}. ${line.note}`;
}

/** The full itemised breakdown, in completion order. */
export function breakdownBullets(out: HomeBuyingCostOutput): string[] {
  return [
    lineBullet(out.dutyLine),
    lineBullet(out.lmiLine),
    ...out.miscLines.map(lineBullet),
    lineBullet(out.fhogLine),
  ];
}

/** The FHSS deposit-contribution bullet — surfaced, never netted; with caveats. */
export function fhssBullet(out: HomeBuyingCostOutput): string | null {
  const f = out.fhssDepositContribution;
  if (!f) return null;
  if (!f.eligible) {
    return "First Home Super Saver: on the facts entered you do not appear to meet all the FHSS gates, so no releasable amount is shown. Check the ATO page.";
  }
  const base =
    `First Home Super Saver: you appear able to release up to ${formatAud(f.maxReleasableContributions)} ` +
    `of eligible voluntary super contributions toward your deposit (a gated maximum, not a payout). ` +
    `This is your own super — it is shown as a deposit contribution, not government help, and is not ` +
    `subtracted from the completion cash above.`;
  const earnings = f.earningsAddedOnRelease
    ? " The ATO adds deemed earnings on top when it finalises the release amount."
    : "";
  const erosion = f.lowIncomeErosionCaveat
    ? " For a lower-income earner the 15% contributions tax on salary-sacrificed amounts can erode the benefit — confirm it suits your circumstances."
    : "";
  return base + earnings + erosion;
}

/** Source/verification line for the composer's own misc figures. */
export function sourceBullet(fy: string, verifiedAt: string): string {
  return (
    `Stamp duty, the First Home Owner Grant and scheme eligibility come from the relevant ` +
    `state revenue office and the federal scheme sites; LMI is an indicative grid; conveyancing, ` +
    `inspection and registration fees are indicative ranges last verified ${verifiedAt} (FY ${fy}). ` +
    `These figures change — always verify before you act.`
  );
}

export function explanationBullets(out: HomeBuyingCostOutput, verifiedAt: string): string[] {
  const fhss = fhssBullet(out);
  return [
    ...breakdownBullets(out),
    ...(fhss ? [fhss] : []),
    ...out.stackingNotes,
    sourceBullet(out.fy, verifiedAt),
    HOME_BUYING_COST_DISCLAIMER,
  ];
}
